import React, { useState, useEffect, useRef, useCallback } from "react";
import Modal from "react-modal";
import Loader from "../../common/Loader";
import EduApi from "../../../services/resources/EduApi";
import { ApiCallErrorMessageHandler } from "../../../lib/coc-common-scripts";
import { formatFullName } from "../../../lib";
import axios from "axios";
import moment from "moment";

export default function CourseScheduleAttendanceModal({
  courseSchedule,
  onClose,
}) {
  const [attendance, setAttendance] = useState([]);
  const [classSchedules, setClassSchedules] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const apiSignal = useRef(axios.CancelToken.source());

  useEffect(() => {
    const signal = apiSignal.current;
    return () => {
      signal.cancel();
    };
  }, []);

  const loadData = useCallback(async () => {
    if (!courseSchedule) {
      return;
    }

    setLoading(true);
    setErrorMessage("");

    const { courseScheduleID, eduChabadHouseEnrollmentID } = courseSchedule;

    try {
      const [scheduleData, attendanceData, studentsData] = await Promise.all([
        EduApi.getCourseSchedule(
          apiSignal.current.token,
          eduChabadHouseEnrollmentID,
          courseScheduleID,
        ),
        EduApi.getCourseScheduleAttendance(
          apiSignal.current.token,
          eduChabadHouseEnrollmentID,
          courseScheduleID,
        ),
        EduApi.getCourseScheduleStudents(
          apiSignal.current.token,
          eduChabadHouseEnrollmentID,
          courseScheduleID,
        ),
      ]);

      const pastClasses = (scheduleData.classSchedules || [])
        .filter((cs) => cs.dateTime && moment(cs.dateTime).isBefore(moment()))
        .sort((a, b) => a.sortOrder - b.sortOrder);

      setClassSchedules(pastClasses);
      setAttendance(attendanceData);
      setStudents(studentsData.results || []);
      setLoading(false);
    } catch (err) {
      if (!axios.isCancel(err)) {
        setErrorMessage(ApiCallErrorMessageHandler(err));
        setLoading(false);
      }
    }
  }, [courseSchedule]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleAttendance = (enrollmentId, classScheduleId) => {
    const exists = attendance.find(
      (a) =>
        a.eduStudentCourseScheduleEnrollmentID === enrollmentId &&
        a.eduClassScheduleID === classScheduleId,
    );

    if (exists) {
      setAttendance(
        attendance.filter(
          (a) =>
            !(
              a.eduStudentCourseScheduleEnrollmentID === enrollmentId &&
              a.eduClassScheduleID === classScheduleId
            ),
        ),
      );
    } else {
      setAttendance([
        ...attendance,
        {
          eduStudentCourseScheduleEnrollmentID: enrollmentId,
          eduClassScheduleID: classScheduleId,
        },
      ]);
    }
  };

  const saveAttendance = async () => {
    if (!courseSchedule) {
      return;
    }

    setSaving(true);
    setErrorMessage("");

    const { courseScheduleID, eduChabadHouseEnrollmentID } = courseSchedule;

    try {
      await EduApi.submitCourseScheduleAttendance(
        apiSignal.current.token,
        eduChabadHouseEnrollmentID,
        courseScheduleID,
        attendance,
      );

      setSaving(false);
      onClose();
    } catch (err) {
      if (!axios.isCancel(err)) {
        setErrorMessage(ApiCallErrorMessageHandler(err));
        setSaving(false);
      }
    }
  };

  const {
    campusName = "",
    chabadHouseName = "",
    name: courseName = "",
    semester = "",
    shliachFirstName = "",
    shliachLastName = "",
    shluchaFirstName = "",
    year = "",
  } = courseSchedule || {};

  const shliachNames = [
    shliachFirstName ? formatFullName(shliachFirstName, shliachLastName) : null,
    shluchaFirstName ? formatFullName(shluchaFirstName, shliachLastName) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Modal isOpen={true} className="modal-container">
      <div
        className="modal-card card"
        style={{ maxWidth: "800px", width: "90vw" }}
      >
        <div className="flex flex-justify-space flex-align-center">
          <p className="xl-text fw-700">
            Manage Attendance{" "}
            <span className="fw-500">
              {courseName} - {`${semester} ${year}`}
            </span>
          </p>
          <i
            className="material-icons link-text large-text"
            onClick={onClose}
            style={{ cursor: "pointer" }}
          >
            close
          </i>
        </div>
        <p className="accent-text-dark medium-text mb-16">
          {[chabadHouseName, campusName, shliachNames]
            .filter(Boolean)
            .join(" · ")}
        </p>

        <hr
          className="mb-16"
          style={{ border: "none", borderTop: "1px solid rgba(0,0,0,0.1)" }}
        />

        {loading ? (
          <Loader />
        ) : errorMessage ? (
          <p className="error-text">{errorMessage}</p>
        ) : students.length === 0 || classSchedules.length === 0 ? (
          <>
            <p className="accent-text-dark medium-text mt-16 mb-16">
              {students.length === 0
                ? "There are no approved students for this course schedule."
                : "There are no past classes to mark attendance for yet."}
            </p>
            <div className="text-right">
              <button className="btn btn-light" onClick={onClose}>
                Close
              </button>
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                overflowX: "auto",
                overflowY: "auto",
                maxHeight: "60vh",
              }}
            >
              <table className="attendance-modal-table">
                <thead>
                  <tr>
                    <th
                      className="attendance-modal-th accent-text-dark"
                      style={{ textAlign: "left" }}
                    >
                      Student
                    </th>
                    {classSchedules.map((cs) => (
                      <th
                        key={cs.id}
                        className="attendance-modal-th accent-text-dark"
                        style={{ textAlign: "center" }}
                      >
                        <div>Class {cs.sortOrder}</div>
                        <div
                          style={{
                            fontWeight: 400,
                            fontSize: "11px",
                            textTransform: "none",
                          }}
                        >
                          {moment(cs.dateTime).format("MMM D, YYYY")}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {students.map((student) => {
                    const enrollmentId =
                      student.eduStudentCourseScheduleEnrollmentID;
                    return (
                      <tr key={enrollmentId}>
                        <td>
                          <div className="fw-500">
                            {formatFullName(
                              student.studentFirstName,
                              student.studentLastName,
                            )}
                          </div>
                        </td>
                        {classSchedules.map((cs) => {
                          const didAttend = attendance.some(
                            (a) =>
                              a.eduStudentCourseScheduleEnrollmentID ===
                                enrollmentId && a.eduClassScheduleID === cs.id,
                          );
                          return (
                            <td key={cs.id} style={{ textAlign: "center" }}>
                              <i
                                className={`material-icons attendance-checkbox ${didAttend ? "attended" : "absent"}`}
                                onClick={() =>
                                  toggleAttendance(enrollmentId, cs.id)
                                }
                              >
                                {didAttend
                                  ? "check_box"
                                  : "check_box_outline_blank"}
                              </i>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex flex-align-center flex-justify-space mt-24">
              <p className="accent-text" style={{ fontSize: "12px" }}>
                <i
                  className="material-icons mr-4"
                  style={{
                    fontSize: "14px",
                    verticalAlign: "middle",
                  }}
                >
                  info
                </i>
                Only approved students shown. Attendance can only be marked for
                past classes.
              </p>
              <div className="flex" style={{ gap: "12px" }}>
                <button
                  className="btn btn-light"
                  onClick={onClose}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-accent"
                  onClick={saveAttendance}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save Attendance"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
